import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
  alpha,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { AdminDialog } from '@/components/AdminDialog'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { DragHandle, Add, Close, Lock, Edit, ContentCopy } from '@mui/icons-material'
import { PrivateUserInfoType, PickupLocation, RepairShopLocation } from '@otw/models'
import { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import {
  ShipmentPlan,
  ShipmentPlanLocation,
  ShipmentPlanLocationType,
  ShipmentLeg,
  LegFile,
  deriveShipmentLegsFromLocations,
  LegMergeContext,
  LegSplitContext,
  getLegStatus,
  appendLegStatus,
  getTruckStatus,
  getShipmentPlanKind,
  type ShipmentPlanKind,
} from '@otw/models/logistics'
import { Timestamp } from 'firebase/firestore'
import { useClientUsers } from 'hooks/useUsers'
import { formatRepairShopCapacityLabel, useLogistics } from 'hooks/logistics/useLogistics'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useRepairShopLocations, filterRepairShopsForPicker, isRepairShopActive } from 'hooks/inventory/useRepairShopLocations'
import { useInventory } from '@/hooks/inventory/useInventory'
import { apiClient } from '@/core/api/apiClient'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { useLeads } from 'hooks/admin/useLeads'
import {
  findClosedLeadForVehicle,
  getClosedLeadDropoffLocationId,
} from 'views/SuperAdmin/Management/LeadManagement/util/leadDropoffValidation'

function isRepairShopLocation(
  loc: PickupLocation | RepairShopLocation
): loc is RepairShopLocation {
  return 'shop_type' in loc && Array.isArray(loc.shop_type)
}

function getLocationLabel(loc: PickupLocation | RepairShopLocation): string {
  if (isRepairShopLocation(loc)) {
    return loc.name || loc.contact_name || 'Unknown Shop'
  }
  return loc.contact_name || 'Unknown Contact'
}

function getLocationAddress(loc: PickupLocation | RepairShopLocation): string {
  const parts = [loc.address, loc.city, loc.state, loc.zip].filter(Boolean)
  return parts.join(', ')
}

function formatDistance(meters: number): string {
  const miles = meters / 1609.344
  if (miles < 0.1) return `${Math.round(meters)} m`
  if (miles < 10) return `${miles.toFixed(1)} mi`
  return `${Math.round(miles)} mi`
}

function calculateDistanceMeters(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
): number {
  const earthRadiusMeters = 6371000
  const toRadians = (value: number) => (value * Math.PI) / 180

  const dLat = toRadians(to.lat - from.lat)
  const dLng = toRadians(to.lng - from.lng)
  const fromLatRadians = toRadians(from.lat)
  const toLatRadians = toRadians(to.lat)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(fromLatRadians) *
      Math.cos(toLatRadians) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return earthRadiusMeters * c
}

function normalizeGatePassUploadedAt(value: unknown): Timestamp {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return Timestamp.fromDate(value)
  }

  if (typeof value === 'string') {
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) {
      return Timestamp.fromDate(parsed)
    }
  }

  return Timestamp.now()
}

function mapVehicleGatePassesToLegFiles(
  gatePasses: unknown
): LegFile[] {
  if (!Array.isArray(gatePasses) || gatePasses.length === 0) return []

  return gatePasses.map((file: any) => ({
    fileName: String(file?.fileName ?? ''),
    storagePath: String(file?.storagePath ?? ''),
    url: String(file?.url ?? ''),
    fileSize: Number(file?.fileSize ?? 0),
    fileType: String(file?.fileType ?? ''),
    uploadedAt: normalizeGatePassUploadedAt(file?.uploadedAt),
    uploadedBy: String(file?.uploadedBy ?? ''),
  }))
}

function attachVehicleGatePassesToFirstLeg(
  shipmentLegs: ShipmentLeg[],
  gatePasses: unknown
): ShipmentLeg[] {
  if (!shipmentLegs.length) return shipmentLegs

  const vehicleGatePasses = mapVehicleGatePassesToLegFiles(gatePasses)
  if (!vehicleGatePasses.length) return shipmentLegs

  const firstLeg = shipmentLegs[0]
  if ((firstLeg.gate_passes ?? []).length > 0) return shipmentLegs

  const nextLegs = [...shipmentLegs]
  nextLegs[0] = {
    ...firstLeg,
    gate_passes: vehicleGatePasses,
  }
  return nextLegs
}

interface ShipmentPlanDialogProps {
  open: boolean
  onClose: () => void
  truckWithVehicle: TransportTruckWithVehicle | null
  planToEdit?: ShipmentPlan | null
  /** When set, prompt user to add repair shop at this leg; used after pre-delivery photo rejection */
  insertRepairAfterLegIndex?: number | null
  /** Used when creating a new plan. Ignored in edit mode. */
  createPlanKind?: ShipmentPlanKind
}

const ShipmentPlanDialog: React.FC<ShipmentPlanDialogProps> = ({
  open,
  onClose,
  truckWithVehicle,
  planToEdit,
  insertRepairAfterLegIndex,
  createPlanKind = 'standard',
}) => {
  const [selectedClientUser, setSelectedClientUser] = useState<PrivateUserInfoType | null>(null)
  const [description, setDescription] = useState('')
  const [locations, setLocations] = useState<ShipmentPlanLocation[]>([])
  const [addLocationDialogOpen, setAddLocationDialogOpen] = useState(false)
  const [isCreatingLocation, setIsCreatingLocation] = useState(false)
  const [creatingLocation, setCreatingLocation] = useState(false)
  const [newLocationError, setNewLocationError] = useState<string | null>(null)
  const [newLocationType, setNewLocationType] = useState<ShipmentPlanLocationType>('pickup')
  const [newLocationName, setNewLocationName] = useState('')
  const [newLocationAddress, setNewLocationAddress] = useState('')
  const [newLocationCity, setNewLocationCity] = useState('')
  const [newLocationState, setNewLocationState] = useState('')
  const [newLocationZip, setNewLocationZip] = useState('')
  const [newLocationContactName, setNewLocationContactName] = useState('')
  const [newLocationContactPhone, setNewLocationContactPhone] = useState('')
  const [editingLocation, setEditingLocation] = useState<ShipmentPlanLocation | null>(null)
  const [nearbyRepairDistances, setNearbyRepairDistances] = useState<Record<string, number>>({})
  const [isLoadingNearbyRepairDistances, setIsLoadingNearbyRepairDistances] = useState(false)
  const [includeInactiveRepairShops, setIncludeInactiveRepairShops] = useState(false)
  const geocodeCacheRef = useRef<Map<string, { lat: number; lng: number } | null>>(new Map())
  const [trackingPageUrl, setTrackingPageUrl] = useState<string | null>(null)
  const [trackingLinkLoadError, setTrackingLinkLoadError] = useState<string | null>(null)
  const [isLoadingTrackingLink, setIsLoadingTrackingLink] = useState(false)

  const { clientUsers, isLoading: isLoadingClientUsers } = useClientUsers()
  const { leads } = useLeads()
  const {
    createShipmentPlan,
    updateShipmentPlan,
    isCreatingShipmentPlan,
    isUpdatingShipmentPlan,
    repairShopAvgDropoffToCompleteMsById,
    repairShopTruckCountsById,
  } = useLogistics()
  const isEditMode = !!planToEdit
  const isPostDeliveryRepairMode = isEditMode
    ? getShipmentPlanKind(planToEdit) === 'post_delivery_repair'
    : createPlanKind === 'post_delivery_repair'
  const { pickupLocations, createPickupLocation, updatePickupLocation } = usePickupLocations()
  const { repairShopLocations, createRepairShopLocation, updateRepairShopLocation } = useRepairShopLocations()
  const { updateDocument } = useInventory('master_inventory')
  const { showSuccess, showError } = useSnackbar()
  const isMobile = useIsMobile()

  const allSavedLocations = useMemo(() => {
    const pickup = pickupLocations || []
    const repair = filterRepairShopsForPicker(repairShopLocations, {
      includeInactive: includeInactiveRepairShops,
    })
    return [...pickup, ...repair] as (PickupLocation | RepairShopLocation)[]
  }, [pickupLocations, repairShopLocations, includeInactiveRepairShops])

  const availableLocations = useMemo(() => {
    let filtered = allSavedLocations
    if (insertRepairAfterLegIndex != null) {
      filtered = filtered.filter(isRepairShopLocation)
    }
    return filtered
  }, [allSavedLocations, insertRepairAfterLegIndex])

  const getSavedLocationByPlanLocation = useCallback(
    (location: ShipmentPlanLocation) => {
      if (location.location_type === 'repair') {
        return repairShopLocations?.find((r) => r.id === location.location_id)
      }
      return pickupLocations?.find((p) => p.id === location.location_id)
    },
    [pickupLocations, repairShopLocations]
  )

  const previousLocationForSuggestion = useMemo(() => {
    if (locations.length === 0) return null

    const insertIndex = insertRepairAfterLegIndex != null ? insertRepairAfterLegIndex + 1 : locations.length
    if (insertIndex <= 0) return null

    const previousPlanLocation = locations[insertIndex - 1]
    if (!previousPlanLocation) return null

    return getSavedLocationByPlanLocation(previousPlanLocation) ?? null
  }, [getSavedLocationByPlanLocation, insertRepairAfterLegIndex, locations])

  const geocodeAddress = useCallback(async (address: string): Promise<{ lat: number; lng: number } | null> => {
    const cacheKey = address.trim().toLowerCase()
    if (!cacheKey) return null

    if (geocodeCacheRef.current.has(cacheKey)) {
      return geocodeCacheRef.current.get(cacheKey) ?? null
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}`
      )
      const data = await response.json()
      const result = data.results?.[0]?.geometry?.location
      const coordinates = result ? { lat: result.lat as number, lng: result.lng as number } : null
      geocodeCacheRef.current.set(cacheKey, coordinates)
      return coordinates
    } catch (error) {
      console.error('Error geocoding address:', error)
      geocodeCacheRef.current.set(cacheKey, null)
      return null
    }
  }, [])

  useEffect(() => {
    if (!addLocationDialogOpen || isCreatingLocation) {
      setNearbyRepairDistances({})
      setIsLoadingNearbyRepairDistances(false)
      return
    }

    if (!previousLocationForSuggestion) {
      setNearbyRepairDistances({})
      setIsLoadingNearbyRepairDistances(false)
      return
    }

    const repairOptions = availableLocations.filter(isRepairShopLocation)
    if (repairOptions.length === 0) {
      setNearbyRepairDistances({})
      setIsLoadingNearbyRepairDistances(false)
      return
    }

    const previousAddress = getLocationAddress(previousLocationForSuggestion)
    if (!previousAddress) {
      setNearbyRepairDistances({})
      setIsLoadingNearbyRepairDistances(false)
      return
    }

    let cancelled = false

    const calculateDistances = async () => {
      setIsLoadingNearbyRepairDistances(true)
      const previousCoordinates = await geocodeAddress(previousAddress)
      if (!previousCoordinates || cancelled) {
        if (!cancelled) {
          setNearbyRepairDistances({})
          setIsLoadingNearbyRepairDistances(false)
        }
        return
      }

      const distanceEntries = await Promise.all(
        repairOptions.map(async (repairLocation) => {
          if (!repairLocation.id) return null
          const repairAddress = getLocationAddress(repairLocation)
          if (!repairAddress) return null
          const repairCoordinates = await geocodeAddress(repairAddress)
          if (!repairCoordinates) return null
          const meters = calculateDistanceMeters(previousCoordinates, repairCoordinates)
          return [repairLocation.id, meters] as const
        })
      )

      if (cancelled) return

      const nextDistances = Object.fromEntries(
        distanceEntries.filter((entry): entry is readonly [string, number] => entry !== null)
      )
      setNearbyRepairDistances(nextDistances)
      setIsLoadingNearbyRepairDistances(false)
    }

    calculateDistances()

    return () => {
      cancelled = true
    }
  }, [
    addLocationDialogOpen,
    isCreatingLocation,
    previousLocationForSuggestion,
    availableLocations,
    geocodeAddress,
  ])

  const orderedAvailableLocations = useMemo(() => {
    const repairs = availableLocations.filter(isRepairShopLocation)
    const nonRepairs = availableLocations.filter((location) => !isRepairShopLocation(location))
    const sortedRepairs = [...repairs].sort((a, b) => {
      const distanceA = a.id ? nearbyRepairDistances[a.id] : undefined
      const distanceB = b.id ? nearbyRepairDistances[b.id] : undefined
      if (distanceA == null && distanceB == null) return 0
      if (distanceA == null) return 1
      if (distanceB == null) return -1
      return distanceA - distanceB
    })

    if (!previousLocationForSuggestion) {
      return availableLocations
    }

    return [...sortedRepairs, ...nonRepairs]
  }, [availableLocations, nearbyRepairDistances, previousLocationForSuggestion])

  const nearestRepairShopSuggestions = useMemo(() => {
    if (!previousLocationForSuggestion) return []

    return availableLocations
      .filter(isRepairShopLocation)
      .filter((shop) => includeInactiveRepairShops || isRepairShopActive(shop))
      .filter((shop) => !!shop.id && nearbyRepairDistances[shop.id] != null)
      .sort((a, b) => {
        const aDistance = nearbyRepairDistances[a.id!]
        const bDistance = nearbyRepairDistances[b.id!]
        return aDistance - bDistance
      })
      .slice(0, 10)
  }, [
    availableLocations,
    nearbyRepairDistances,
    previousLocationForSuggestion,
    includeInactiveRepairShops,
  ])

  const formatAverageDuration = (durationMs?: number) => {
    if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) return 'N/A'
    const totalMinutes = Math.round(durationMs / (1000 * 60))
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
    const minutes = totalMinutes % 60
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
      }),
    []
  )

  const getCapacityLabel = useCallback(
    (shop: RepairShopLocation): string => {
      const counts = shop.id ? repairShopTruckCountsById[shop.id] : undefined
      return formatRepairShopCapacityLabel(shop, counts, numberFormatter)
    },
    [numberFormatter, repairShopTruckCountsById]
  )

  const lockedLocationCount = useMemo(() => {
    if (!isEditMode || !planToEdit) return 0
    const legs = planToEdit.shipment_legs ?? []
    let completedPrefixCount = 0
    for (const leg of legs) {
      if (getLegStatus(leg) !== 'Completed') break
      completedPrefixCount += 1
    }
    // If first N legs are completed, first N+1 locations are fixed history/current path and must stay immutable.
    // When no legs are completed yet (e.g. draft/assigned and truck has not moved), nothing should be locked.
    if (completedPrefixCount === 0) return 0
    return Math.min(completedPrefixCount + 1, locations.length)
  }, [isEditMode, planToEdit, locations.length])

  const isLocationLocked = useCallback(
    (index: number) => index < lockedLocationCount,
    [lockedLocationCount]
  )

  const locationsWithDetails = useMemo(() => {
    return locations.map((loc) => {
      const found =
        loc.location_type === 'repair'
          ? repairShopLocations?.find((r) => r.id === loc.location_id)
          : pickupLocations?.find((p) => p.id === loc.location_id)
      return {
        ...loc,
        label: found ? getLocationLabel(found) : 'Unknown',
        address: found ? getLocationAddress(found) : '',
      }
    })
  }, [locations, pickupLocations, repairShopLocations])

  const clientOptions = useMemo(() => {
    const list = clientUsers || []
    const getKey = (opt: PrivateUserInfoType) =>
      opt.id ?? `${opt.first_name}-${opt.last_name}-${opt.email ?? ''}`
    const seen = new Set<string>()
    return list.filter((opt) => {
      const key = getKey(opt)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [clientUsers])

  const getClientOptionKey = useCallback(
    (option: PrivateUserInfoType) =>
      option.id ?? `${option.first_name}-${option.last_name}-${option.email ?? ''}`,
    []
  )

  const suggestedClientUser = useMemo(() => {
    const vehicle = truckWithVehicle?.vehicle
    if (!vehicle) return null

    const suggestedClient = vehicle.sold_to_user ?? vehicle.selling_to_user
    if (!suggestedClient) return null

    const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? ''
    const suggestedId = normalize(suggestedClient.id)
    const suggestedName = normalize(suggestedClient.name)

    const byId =
      (suggestedId &&
        clientOptions.find(
          (clientUser) =>
            normalize(clientUser.id) === suggestedId ||
            normalize(clientUser.firestoreDocId) === suggestedId
        )) ||
      null
    if (byId) return byId

    if (!suggestedName) return null

    return (
      clientOptions.find((clientUser) => {
        const fullName = normalize(`${clientUser.first_name} ${clientUser.last_name}`)
        return (
          fullName === suggestedName ||
          normalize(clientUser.displayName) === suggestedName ||
          normalize(clientUser.company_name) === suggestedName
        )
      }) ?? null
    )
  }, [truckWithVehicle?.vehicle, clientOptions])

  const suggestedClientOptionKey = useMemo(
    () => (suggestedClientUser ? getClientOptionKey(suggestedClientUser) : null),
    [suggestedClientUser, getClientOptionKey]
  )

  const resetForm = () => {
    setSelectedClientUser(null)
    setDescription('')
    setLocations([])
    setAddLocationDialogOpen(false)
    setIsCreatingLocation(false)
    setCreatingLocation(false)
    setNewLocationError(null)
    setNewLocationType('pickup')
    setNewLocationName('')
    setNewLocationAddress('')
    setNewLocationCity('')
    setNewLocationState('')
    setNewLocationZip('')
    setNewLocationContactName('')
    setNewLocationContactPhone('')
    setEditingLocation(null)
  }

  useEffect(() => {
    if (!open) {
      resetForm()
    } else if (planToEdit) {
      setDescription(planToEdit.description ?? '')
      setLocations(planToEdit.locations ?? [])
    } else {
      resetForm()
    }
  }, [open, planToEdit])

  useEffect(() => {
    if (!open || !planToEdit) return
    const client = planToEdit.client_user_id
      ? clientUsers?.find((u) => u.id === planToEdit.client_user_id)
      : null
    setSelectedClientUser(client ?? null)
  }, [open, planToEdit, clientUsers])

  useEffect(() => {
    if (!open || isEditMode) return
    if (selectedClientUser || !suggestedClientUser) return
    setSelectedClientUser(suggestedClientUser)
  }, [open, isEditMode, selectedClientUser, suggestedClientUser])

  useEffect(() => {
    if (!open || !selectedClientUser?.id) {
      setTrackingPageUrl(null)
      setTrackingLinkLoadError(null)
      setIsLoadingTrackingLink(false)
      return
    }
    let cancelled = false
    setIsLoadingTrackingLink(true)
    setTrackingLinkLoadError(null)
    setTrackingPageUrl(null)
    apiClient.portal.admin
      .createDeliveryTrackingToken({ client_user_id: selectedClientUser.id })
      .then((res) => {
        if (!cancelled) setTrackingPageUrl(res.tracking_page_url)
      })
      .catch((e) => {
        if (!cancelled) {
          setTrackingLinkLoadError(e instanceof Error ? e.message : 'Failed to load tracking link')
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTrackingLink(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, selectedClientUser?.id])

  useEffect(() => {
    if (!open || isEditMode || insertRepairAfterLegIndex != null) return
    if (locations.length > 0) return

    if (isPostDeliveryRepairMode) {
      const previousPlans = [...(truckWithVehicle?.shipmentPlans ?? [])].sort((a, b) => {
        const aMs =
          typeof a.created_at?.toMillis === 'function'
            ? a.created_at.toMillis()
            : a.created_at
              ? new Date(a.created_at as unknown as string).getTime()
              : 0
        const bMs =
          typeof b.created_at?.toMillis === 'function'
            ? b.created_at.toMillis()
            : b.created_at
              ? new Date(b.created_at as unknown as string).getTime()
              : 0
        return bMs - aMs
      })
      const previousPlan = previousPlans[0]
      const lastLocation = previousPlan?.locations?.[previousPlan.locations.length - 1]
      if (lastLocation?.location_id) {
        setLocations([
          {
            location_id: lastLocation.location_id,
            location_type: lastLocation.location_type ?? 'pickup',
            notes: lastLocation.notes,
          },
        ])
        return
      }
    }

    const vehicleId = truckWithVehicle?.vehicle?.id
    const inventoryPickupLocation = truckWithVehicle?.vehicle?.pickup_location

    const matchedPickupLocation = inventoryPickupLocation
      ? pickupLocations?.find((pickup) => {
          if (inventoryPickupLocation.id && pickup.id === inventoryPickupLocation.id) return true
          return (
            pickup.address === inventoryPickupLocation.address &&
            pickup.city === inventoryPickupLocation.city &&
            pickup.state === inventoryPickupLocation.state &&
            pickup.zip === inventoryPickupLocation.zip
          )
        })
      : undefined

    const closedLead = vehicleId ? findClosedLeadForVehicle(leads, vehicleId) : null
    const dropoffLocationId =
      closedLead && vehicleId ? getClosedLeadDropoffLocationId(closedLead, vehicleId) : undefined

    const initialLocations: ShipmentPlanLocation[] = []

    if (matchedPickupLocation?.id) {
      initialLocations.push({
        location_id: matchedPickupLocation.id,
        location_type: 'pickup',
        notes: undefined,
      })
    }

    if (
      dropoffLocationId &&
      dropoffLocationId !== matchedPickupLocation?.id &&
      !initialLocations.some((loc) => loc.location_id === dropoffLocationId)
    ) {
      initialLocations.push({
        location_id: dropoffLocationId,
        location_type: 'pickup',
        notes: undefined,
      })
    }

    if (initialLocations.length > 0) {
      setLocations(initialLocations)
    }
  }, [
    open,
    isEditMode,
    isPostDeliveryRepairMode,
    insertRepairAfterLegIndex,
    locations.length,
    truckWithVehicle?.shipmentPlans,
    truckWithVehicle?.vehicle?.id,
    truckWithVehicle?.vehicle?.pickup_location,
    pickupLocations,
    leads,
  ])

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleNotifyInventoryForPickupLocation = useCallback(() => {
    const vehicle = truckWithVehicle?.vehicle
    if (!vehicle?.id || vehicle.refinance === true) return

    updateDocument({
      id: vehicle.id,
      data: {
        ready_for_transport: {
          ...vehicle.ready_for_transport,
          needs_address: true,
          ready_for_pickup: vehicle.ready_for_transport?.ready_for_pickup ?? false,
        },
      },
    })
  }, [truckWithVehicle?.vehicle, updateDocument])

  const openAddLocationDialog = useCallback(() => {
    setNewLocationError(null)
    setIsCreatingLocation(false)
    setNewLocationType(
      insertRepairAfterLegIndex != null || isPostDeliveryRepairMode ? 'repair' : 'pickup'
    )
    setNewLocationName('')
    setNewLocationAddress('')
    setNewLocationCity('')
    setNewLocationState('')
    setNewLocationZip('')
    setNewLocationContactName('')
    setNewLocationContactPhone('')
    setEditingLocation(null)
    setAddLocationDialogOpen(true)
  }, [insertRepairAfterLegIndex, isPostDeliveryRepairMode])

  const openEditLocationDialog = useCallback(
    (location: ShipmentPlanLocation, index: number) => {
      if (isLocationLocked(index)) return
      const savedLocation = getSavedLocationByPlanLocation(location)
      if (!savedLocation) {
        setNewLocationError('Could not load location details for editing.')
        return
      }

      setNewLocationError(null)
      setIsCreatingLocation(true)
      setEditingLocation(location)
      setNewLocationType(location.location_type)
      setNewLocationName(
        location.location_type === 'repair' && 'name' in savedLocation
          ? (savedLocation.name as string | undefined) || ''
          : ''
      )
      setNewLocationAddress(savedLocation.address || '')
      setNewLocationCity(savedLocation.city || '')
      setNewLocationState(savedLocation.state || '')
      setNewLocationZip(savedLocation.zip || '')
      setNewLocationContactName(savedLocation.contact_name || '')
      setNewLocationContactPhone(savedLocation.contact_phone || '')
      setAddLocationDialogOpen(true)
    },
    [getSavedLocationByPlanLocation, isLocationLocked]
  )

  const handleSelectLocation = useCallback(
    (loc: PickupLocation | RepairShopLocation | null) => {
      const locId = loc?.id
      if (!locId) return
      const locationType = isRepairShopLocation(loc) ? 'repair' : 'pickup'
      const newLoc: ShipmentPlanLocation = {
        location_id: locId,
        location_type: locationType as ShipmentPlanLocationType,
        notes: undefined,
      }
      if (insertRepairAfterLegIndex != null) {
        const insertIndex = insertRepairAfterLegIndex + 1
        if (insertIndex < lockedLocationCount) return
        setLocations((prev) => [...prev.slice(0, insertIndex), newLoc, ...prev.slice(insertIndex)])
      } else {
        setLocations((prev) => [...prev, newLoc])
      }
      setAddLocationDialogOpen(false)
    },
    [insertRepairAfterLegIndex, lockedLocationCount]
  )

  const handleRemoveLocation = useCallback((index: number) => {
    if (isLocationLocked(index)) return
    setLocations((prev) => prev.filter((_, i) => i !== index))
  }, [isLocationLocked])

  const handleUpdateLocationNotes = useCallback((index: number, notes: string) => {
    if (isLocationLocked(index)) return
    setLocations((prev) =>
      prev.map((l, i) => (i === index ? { ...l, notes: notes || undefined } : l))
    )
  }, [isLocationLocked])

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return
    const from = result.source.index
    const to = result.destination.index
    if (from === to) return
    if (isLocationLocked(from) || to < lockedLocationCount) return
    setLocations((prev) => {
      const next = [...prev]
      const [removed] = next.splice(from, 1)
      next.splice(to, 0, removed)
      return next
    })
  }, [isLocationLocked, lockedLocationCount])

  /**
   * Detect split-like edits so leg inheritance behaves consistently.
   * Supports:
   * 1) one inserted location (old length + 1)
   * 2) one in-place replacement of an internal location (same length)
   */
  const getSplitContext = useCallback((): LegSplitContext | undefined => {
    if (!isEditMode || !planToEdit) return undefined
    const oldLocs = planToEdit.locations ?? []
    const isSameLocation = (a?: ShipmentPlanLocation, b?: ShipmentPlanLocation) =>
      !!a &&
      !!b &&
      a.location_id === b.location_id &&
      a.location_type === b.location_type

    // Case 1: exactly one inserted location.
    if (locations.length === oldLocs.length + 1) {
      let insertIndex: number | null = null
      for (let i = 0; i < locations.length; i++) {
        if (i >= oldLocs.length) {
          insertIndex = i
          break
        }
        const o = oldLocs[i]
        const n = locations[i]
        if (!isSameLocation(o, n)) {
          insertIndex = i
          break
        }
      }
      if (insertIndex == null || insertIndex < 1 || insertIndex > oldLocs.length - 1) return undefined
      return { splitLegIndex: insertIndex - 1 }
    }

    // Case 2: one location replaced in place (delete + add in same slot).
    // Treat like splitting the leg between its surrounding unchanged path.
    if (locations.length === oldLocs.length && locations.length >= 2) {
      const changedIndices: number[] = []
      for (let i = 0; i < locations.length; i++) {
        if (!isSameLocation(oldLocs[i], locations[i])) changedIndices.push(i)
      }
      if (changedIndices.length !== 1) return undefined
      const replacedIndex = changedIndices[0]
      if (replacedIndex < 1) return undefined

      const previousUnchanged = isSameLocation(
        oldLocs[replacedIndex - 1],
        locations[replacedIndex - 1]
      )
      if (!previousUnchanged) return undefined

      // For internal replacements require next neighbor unchanged.
      // For tail replacement, only previous neighbor exists/needs validation.
      const isTailReplacement = replacedIndex === locations.length - 1
      const nextUnchanged = isTailReplacement
        ? true
        : isSameLocation(oldLocs[replacedIndex + 1], locations[replacedIndex + 1])
      if (!nextUnchanged) {
        return undefined
      }
      return { splitLegIndex: replacedIndex - 1 }
    }

    return undefined
  }, [isEditMode, planToEdit, locations])

  /**
   * Detect removal of a single internal stop: same as merging two legs into one.
   * The new leg that replaces (removed → next) inherits from the first old leg (same as the first new leg in a split).
   */
  const getMergeContext = useCallback((): LegMergeContext | undefined => {
    if (!isEditMode || !planToEdit) return undefined
    const oldLocs = planToEdit.locations ?? []
    if (oldLocs.length !== locations.length + 1) return undefined
    const isSameLocation = (a?: ShipmentPlanLocation, b?: ShipmentPlanLocation) =>
      !!a &&
      !!b &&
      a.location_id === b.location_id &&
      a.location_type === b.location_type

    for (let r = 0; r < oldLocs.length; r++) {
      const merged = oldLocs.filter((_, i) => i !== r)
      if (merged.length !== locations.length) continue
      let allMatch = true
      for (let j = 0; j < locations.length; j++) {
        if (!isSameLocation(merged[j], locations[j])) {
          allMatch = false
          break
        }
      }
      if (!allMatch) continue
      if (r < 1 || r > oldLocs.length - 2) return undefined
      return { mergeSourceLegIndex: r - 1 }
    }
    return undefined
  }, [isEditMode, planToEdit, locations])

  const handleCreateAndSelectLocation = useCallback(async () => {
    if (!newLocationAddress.trim() || !newLocationCity.trim() || !newLocationState.trim() || !newLocationZip.trim()) {
      setNewLocationError('Address, city, state, and zip are required.')
      return
    }
    if (newLocationType === 'repair' && !newLocationName.trim()) {
      setNewLocationError('Shop name is required for repair locations.')
      return
    }

    setNewLocationError(null)
    setCreatingLocation(true)
    try {
      if (editingLocation) {
        const savedLocation = getSavedLocationByPlanLocation(editingLocation)
        if (!savedLocation) {
          throw new Error('Could not find the original location to update.')
        }

        if (editingLocation.location_type === 'repair') {
          const repairLocation = savedLocation as RepairShopLocation
          await updateRepairShopLocation({
            ...repairLocation,
            name: newLocationName.trim() || undefined,
            address: newLocationAddress.trim(),
            city: newLocationCity.trim(),
            state: newLocationState.trim(),
            zip: newLocationZip.trim(),
            contact_name: newLocationContactName.trim() || undefined,
            contact_phone: newLocationContactPhone.trim() || undefined,
            shop_type: repairLocation.shop_type?.length ? repairLocation.shop_type : ['other'],
          })
        } else {
          await updatePickupLocation({
            ...savedLocation,
            address: newLocationAddress.trim(),
            city: newLocationCity.trim(),
            state: newLocationState.trim(),
            zip: newLocationZip.trim(),
            contact_name: newLocationContactName.trim() || undefined,
            contact_phone: newLocationContactPhone.trim() || undefined,
          })
        }
        setAddLocationDialogOpen(false)
        setEditingLocation(null)
      } else {
        if (newLocationType === 'repair') {
          const created = await createRepairShopLocation({
            name: newLocationName.trim() || undefined,
            address: newLocationAddress.trim(),
            city: newLocationCity.trim(),
            state: newLocationState.trim(),
            zip: newLocationZip.trim(),
            contact_name: newLocationContactName.trim() || undefined,
            contact_phone: newLocationContactPhone.trim() || undefined,
            shop_type: ['other'],
          })
          handleSelectLocation(created)
        } else {
          const created = await createPickupLocation({
            address: newLocationAddress.trim(),
            city: newLocationCity.trim(),
            state: newLocationState.trim(),
            zip: newLocationZip.trim(),
            contact_name: newLocationContactName.trim() || undefined,
            contact_phone: newLocationContactPhone.trim() || undefined,
          })
          handleSelectLocation(created)
        }
      }
    } catch (error) {
      setNewLocationError(
        error instanceof Error
          ? error.message
          : editingLocation
            ? 'Failed to update location'
            : 'Failed to create location'
      )
    } finally {
      setCreatingLocation(false)
    }
  }, [
    newLocationAddress,
    newLocationCity,
    newLocationState,
    newLocationZip,
    newLocationType,
    newLocationName,
    newLocationContactName,
    newLocationContactPhone,
    createPickupLocation,
    createRepairShopLocation,
    updatePickupLocation,
    updateRepairShopLocation,
    handleSelectLocation,
    editingLocation,
    getSavedLocationByPlanLocation,
  ])

  const handleSubmit = async () => {
    if (!truckWithVehicle?.truck.id) return
    const planKind: ShipmentPlanKind = isPostDeliveryRepairMode
      ? 'post_delivery_repair'
      : getShipmentPlanKind(planToEdit)

    if (isPostDeliveryRepairMode) {
      const hasRepairShop = locations.some((loc) => loc.location_type === 'repair')
      if (!hasRepairShop) {
        showError('Add a repair shop for this post-delivery repair')
        return
      }
      if (locations.length < 2) {
        showError('Keep the customer location and add a repair shop (assign transport on each leg)')
        return
      }
    }

    try {
      let existingLegs = isEditMode ? planToEdit?.shipment_legs ?? [] : undefined
      if (
        isEditMode &&
        planToEdit &&
        insertRepairAfterLegIndex != null &&
        existingLegs &&
        existingLegs[insertRepairAfterLegIndex]
      ) {
        const leg = existingLegs[insertRepairAfterLegIndex]
        const withRejection = appendLegStatus(
          leg,
          getLegStatus(leg),
          Timestamp.now(),
          'Pre-delivery photos rejected - needs repair'
        )
        existingLegs = [
          ...existingLegs.slice(0, insertRepairAfterLegIndex),
          withRejection,
          ...existingLegs.slice(insertRepairAfterLegIndex + 1),
        ]
      }
      const splitContext = getSplitContext()
      const mergeContext = getMergeContext()
      const shipment_legs = deriveShipmentLegsFromLocations(
        locations,
        existingLegs,
        splitContext,
        mergeContext
      )
      const shipmentLegsWithVehicleGatePasses = attachVehicleGatePassesToFirstLeg(
        shipment_legs,
        (truckWithVehicle.vehicle as any)?.gate_passes
      )
      if (isEditMode && planToEdit) {
        await updateShipmentPlan({
          planId: planToEdit.id,
          client_user_id: selectedClientUser?.id ?? null,
          description: description.trim() || null,
          locations,
          shipment_legs: shipmentLegsWithVehicleGatePasses,
        })
      } else {
        await createShipmentPlan({
          transportTruckId: truckWithVehicle.truck.id,
          clientUserId: selectedClientUser?.id,
          description: description.trim() || undefined,
          plan_kind: planKind,
          locations,
          shipment_legs: shipmentLegsWithVehicleGatePasses,
        })
      }
      handleClose()
    } catch {
      // Error handled by mutation
    }
  }

  const isSubmitting = isCreatingShipmentPlan || isUpdatingShipmentPlan

  useEffect(() => {
    const initialCount = planToEdit?.locations?.length ?? 0
    if (
      open &&
      insertRepairAfterLegIndex != null &&
      locations.length === initialCount &&
      initialCount > 0
    ) {
      setAddLocationDialogOpen(true)
    }
  }, [open, insertRepairAfterLegIndex, locations.length, planToEdit?.locations?.length])

  return (
    <AdminDialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: isMobile ? { height: '100dvh', maxHeight: '100dvh' } : undefined,
      }}
    >
      <DialogTitle>
        {isEditMode
          ? isPostDeliveryRepairMode
            ? 'Edit Post-Delivery Repair Plan'
            : 'Edit Shipment Plan'
          : isPostDeliveryRepairMode
            ? 'Create Post-Delivery Repair Plan'
            : 'Create Shipment Plan'}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: { xs: 2, md: 3 }, ...(isMobile ? { flex: 1, minHeight: 0, overflow: 'auto' } : {}) }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {insertRepairAfterLegIndex != null && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Pre-delivery photos were rejected. Add a repair shop between the pickup and dropoff of leg{' '}
              {insertRepairAfterLegIndex + 1}. The repair shop will be inserted in the correct position.
            </Alert>
          )}
          {isPostDeliveryRepairMode && insertRepairAfterLegIndex == null && (
            <Alert severity="info">
              Use this plan when a delivered truck needs to go back to a shop. The first stop is the
              customer location from the previous plan — add the repair shop next, then assign
              transport on each leg (including Customer transport when the customer is moving the
              truck). Repair work starts at Awaiting estimate.
            </Alert>
          )}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Client (optional)
            </Typography>
            <Autocomplete<PrivateUserInfoType, false, false, false>
              value={selectedClientUser}
              options={clientOptions}
              getOptionKey={getClientOptionKey}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
              onChange={(_, newValue) => setSelectedClientUser(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select a client"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingClientUsers && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {option.first_name} {option.last_name}
                      </Typography>
                      {suggestedClientOptionKey &&
                        getClientOptionKey(option) === suggestedClientOptionKey && (
                          <Chip
                            label="Suggested"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {option.email}
                    </Typography>
                    {option.company_name && (
                      <Typography variant="caption" color="text.secondary">
                        Company: {option.company_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isLoadingClientUsers}
              noOptionsText={
                isLoadingClientUsers ? 'Loading clients...' : 'No clients found'
              }
            />
            {selectedClientUser && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Delivery tracking
                </Typography>
                {isLoadingTrackingLink && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Preparing link…
                    </Typography>
                  </Box>
                )}
                {!isLoadingTrackingLink && trackingLinkLoadError && (
                  <Alert severity="error" sx={{ py: 0.5 }}>
                    {trackingLinkLoadError}
                  </Alert>
                )}
                {!isLoadingTrackingLink && trackingPageUrl && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'stretch' }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={trackingPageUrl}
                      InputProps={{ readOnly: true }}
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopy fontSize="small" />}
                      sx={{ flexShrink: 0, textTransform: 'none', whiteSpace: 'nowrap' }}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(trackingPageUrl)
                          showSuccess('Link copied to clipboard.')
                        } catch {
                          showError('Could not copy to clipboard.')
                        }
                      }}
                    >
                      Copy link
                    </Button>
                  </Stack>
                )}
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Description (optional)
            </Typography>
            <TextField
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              placeholder="Add any notes or description..."
              size="small"
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Locations
            </Typography>
            {lockedLocationCount > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {lockedLocationCount} location{lockedLocationCount === 1 ? '' : 's'} locked because
                earlier legs are completed. You can only edit locations after the current location.
              </Alert>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="shipment-locations" direction="vertical">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      minHeight: 100,
                    }}
                  >
                    {locationsWithDetails.map((loc, index) => (
                      <Draggable
                        key={`${loc.location_type}_${loc.location_id}_${index}`}
                        draggableId={`${loc.location_type}_${loc.location_id}_${index}`}
                        index={index}
                        isDragDisabled={isLocationLocked(index)}
                      >
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              flexShrink: 0,
                              minWidth: 0,
                              p: 1.5,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: snapshot.isDragging
                                ? (t) => alpha(t.palette.primary.main, 0.08)
                                : isLocationLocked(index)
                                  ? (t) => alpha(t.palette.grey[500], 0.08)
                                  : 'background.paper',
                              opacity: isLocationLocked(index) ? 0.8 : 1,
                              boxShadow: snapshot.isDragging ? 2 : 0,
                              transition: 'box-shadow 0.2s',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 0.5,
                                mb: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: 'text.secondary',
                                    minWidth: 20,
                                  }}
                                >
                                  {index + 1}.
                                </Typography>
                                <Box
                                  {...provided.dragHandleProps}
                                  sx={{
                                    cursor: isLocationLocked(index) ? 'not-allowed' : 'grab',
                                    color: 'text.secondary',
                                  }}
                                >
                                  <DragHandle fontSize="small" />
                                </Box>
                                {isLocationLocked(index) && (
                                  <Lock fontSize="inherit" color="disabled" />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => openEditLocationDialog(loc, index)}
                                  sx={{ p: 0.25 }}
                                  disabled={isLocationLocked(index)}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveLocation(index)}
                                  sx={{ p: 0.25 }}
                                  disabled={isLocationLocked(index)}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2" fontWeight={600} noWrap title={loc.label}>
                                {loc.label}
                              </Typography>
                              {loc.location_type === 'repair' && (
                                <Chip
                                  label="Repair"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    bgcolor: 'error.100',
                                    color: 'error.dark',
                                  }}
                                />
                              )}
                            </Box>
                            {loc.address && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                              >
                                {loc.address}
                              </Typography>
                            )}
                            <TextField
                              size="small"
                              placeholder="Notes"
                              value={loc.notes ?? ''}
                              multiline
                              rows={2}
                              onChange={(e) => handleUpdateLocationNotes(index, e.target.value)}
                              fullWidth
                              disabled={isLocationLocked(index)}
                              sx={{ mt: 1 }}
                              inputProps={{ style: { fontSize: '0.75rem' } }}
                            />
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {locations.length === 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box
                          sx={{
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 2,
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                          }}
                        >
                          No locations yet
                        </Box>
                        {truckWithVehicle?.vehicle?.refinance === true ? (
                          <Typography variant="body2" color="text.secondary" textAlign="center">
                            Refinance trucks do not get a pickup address.
                          </Typography>
                        ) : truckWithVehicle?.vehicle?.id ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                              variant="contained"
                              color="warning"
                              onClick={handleNotifyInventoryForPickupLocation}
                              sx={{ textTransform: 'none' }}
                            >
                              Notify inventory
                            </Button>
                          </Box>
                        ) : null}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      <Button
                        startIcon={<Add />}
                        onClick={openAddLocationDialog}
                        sx={{
                          textTransform: 'none',
                          color: 'text.primary',
                        }}
                      >
                        Add location
                      </Button>
                    </Box>
                  </Box>
                )}
              </Droppable>
            </DragDropContext>

            <Dialog
              open={addLocationDialogOpen}
              onClose={() => {
                setAddLocationDialogOpen(false)
                setEditingLocation(null)
                setNewLocationError(null)
              }}
              maxWidth="sm"
              fullWidth
              fullScreen={isMobile}
              PaperProps={{
                sx: {
                  display: 'flex',
                  flexDirection: 'column',
                  ...(isMobile
                    ? { height: '100dvh', maxHeight: '100dvh' }
                    : { height: 600, maxHeight: 600 }),
                },
              }}
            >
              <DialogTitle>
                {editingLocation ? 'Edit location in location management' : 'Add location'}
              </DialogTitle>
              <Divider />
              <DialogContent sx={{ pt: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
                {!isCreatingLocation ? (
                  <>
                    <Autocomplete<PickupLocation | RepairShopLocation, false, false, false>
                      autoFocus
                      options={orderedAvailableLocations}
                      getOptionKey={(opt) => `${isRepairShopLocation(opt) ? 'repair' : 'pickup'}_${opt.id}`}
                      getOptionLabel={(opt) => getLocationLabel(opt) + (opt.address ? ` - ${getLocationAddress(opt)}` : '')}
                      onChange={(_, v) => handleSelectLocation(v)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Search locations..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingNearbyRepairDistances && <CircularProgress size={16} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {getLocationLabel(option)}
                                {isRepairShopLocation(option) &&
                                (option as RepairShopLocation).is_active === false
                                  ? ' (inactive)'
                                  : ''}
                              </Typography>
                              {isRepairShopLocation(option) && (
                                <Chip
                                  label="Repair"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    bgcolor: 'error.100',
                                    color: 'error.dark',
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {getLocationAddress(option)}
                            </Typography>
                            {isRepairShopLocation(option) &&
                              previousLocationForSuggestion &&
                              option.id &&
                              nearbyRepairDistances[option.id] != null && (
                                <Typography variant="caption" color="primary.main">
                                  {formatDistance(nearbyRepairDistances[option.id])} from previous location
                                </Typography>
                              )}
                            {isRepairShopLocation(option) && option.id && (
                              <Typography variant="caption" color="secondary.main" sx={{ display: 'block' }}>
                                Repair time: {formatAverageDuration(repairShopAvgDropoffToCompleteMsById[option.id])}
                              </Typography>
                            )}
                            {isRepairShopLocation(option) && (
                              <Typography variant="caption" color="text.primary" sx={{ display: 'block' }}>
                                Capacity: {getCapacityLabel(option)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      noOptionsText="No locations found or all locations already added"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeInactiveRepairShops}
                          onChange={(e) => setIncludeInactiveRepairShops(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Include inactive shops"
                      sx={{ mt: 0.5 }}
                    />
                    {previousLocationForSuggestion && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Repair shops are suggested by distance from {getLocationLabel(previousLocationForSuggestion)}.
                      </Typography>
                    )}
                    {nearestRepairShopSuggestions.length > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="subtitle2">Nearest repair shops</Typography>
                        {nearestRepairShopSuggestions.map((shop) => (
                          <Card key={shop.id} variant="outlined">
                            <CardActionArea onClick={() => handleSelectLocation(shop)}>
                              <CardContent sx={{ py: 1.25 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                  }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap title={getLocationLabel(shop)}>
                                      {getLocationLabel(shop)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                      noWrap
                                      title={getLocationAddress(shop)}
                                    >
                                      {getLocationAddress(shop)}
                                    </Typography>
                                  </Box>
                                  {shop.id && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                      <Chip
                                        size="small"
                                        color="primary"
                                        label={formatDistance(nearbyRepairDistances[shop.id])}
                                      />
                                      <Chip
                                        size="small"
                                        color="secondary"
                                        label={`Repair time: ${formatAverageDuration(repairShopAvgDropoffToCompleteMsById[shop.id])}`}
                                      />
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`Capacity: ${getCapacityLabel(shop)}`}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <FormControl
                      size="small"
                      disabled={insertRepairAfterLegIndex != null || editingLocation != null}
                    >
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={newLocationType}
                        label="Type"
                        onChange={(e) => setNewLocationType(e.target.value as ShipmentPlanLocationType)}
                      >
                        <MenuItem value="pickup">Pickup/Dropoff</MenuItem>
                        <MenuItem value="repair">Repair</MenuItem>
                      </Select>
                    </FormControl>
                    {newLocationType === 'repair' && (
                      <TextField
                        size="small"
                        label="Shop name"
                        required
                        value={newLocationName}
                        onChange={(e) => setNewLocationName(e.target.value)}
                      />
                    )}
                    <TextField
                      size="small"
                      label="Address"
                      required
                      value={newLocationAddress}
                      onChange={(e) => setNewLocationAddress(e.target.value)}
                    />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                        gap: 1,
                      }}
                    >
                      <TextField
                        size="small"
                        label="City"
                        required
                        value={newLocationCity}
                        onChange={(e) => setNewLocationCity(e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="State"
                        required
                        value={newLocationState}
                        onChange={(e) => setNewLocationState(e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="Zip"
                        required
                        value={newLocationZip}
                        onChange={(e) => setNewLocationZip(e.target.value)}
                      />
                    </Box>
                    <TextField
                      size="small"
                      label="Contact name"
                      value={newLocationContactName}
                      onChange={(e) => setNewLocationContactName(e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Contact phone"
                      value={newLocationContactPhone}
                      onChange={(e) => setNewLocationContactPhone(e.target.value)}
                    />
                    {newLocationError && <Alert severity="error">{newLocationError}</Alert>}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setNewLocationError(null)
                          setIsCreatingLocation(false)
                          setEditingLocation(null)
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleCreateAndSelectLocation}
                        disabled={creatingLocation}
                      >
                        {creatingLocation
                          ? (editingLocation ? 'Saving...' : 'Creating...')
                          : (editingLocation ? 'Save' : 'Create and add')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </DialogContent>
              <Divider />
              <DialogActions sx={{ p: 2 }}>
                {!isCreatingLocation && (
                  <Button
                    onClick={() => {
                      setNewLocationError(null)
                      setIsCreatingLocation(true)
                    }}
                    variant="contained"
                    sx={{ textTransform: 'uppercase' }}
                  >
                    Create new location
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setAddLocationDialogOpen(false)
                    setEditingLocation(null)
                    setNewLocationError(null)
                  }}
                >
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="secondary" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {isSubmitting ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Plan' : 'Create Plan')}
        </Button>
      </DialogActions>
    </AdminDialog>
  )
}

export default ShipmentPlanDialog
